import { Component, inject, Input, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	ButtonIconComponent,
	DividerComponent,
	EmptyStateComponent,
	ITagIcon,
	ItemComponent,
	LinkComponent,
	ListDetailComponent,
	TagComponent,
	ModalComponent,
	InfoGroupComponent,
	TileComponent,
	IconComponent,
	TableComponent,
} from '@tecalisdev/infinity';
import {
	BOOLEAN_STATUS,
	IOperationOcrDetail,
	IProcessActivity,
} from '../../../../../processes/models/processes.models';
import { SafeDatePipe } from '../../../../../core/pipes/safe-date.pipe';
import { MenuItemAction } from '../../../../models/master.models';
import { RolePermissionsService } from '../../../../../core/services/role-permissions.service';
import { ModalJsonViewerComponent } from '../../../modal-json-viewer/modal-json-viewer.component';
import { LanguageService } from '../../../../../core/services/language.service';
import { normalizePolicyModalInfo, PolicyModalInfo, policyOriginLabel, resolvePolicyOrigin } from '../../../../../core/utils/policy-check.utils';
import { FormOperationDetailSectionsComponent } from '../../../../../operations/components/form-operation-detail-sections/form-operation-detail-sections.component';
import {
	IFormOperation,
	IFormOperationAnswerStep,
	IOperationFormDetail,
} from '../../../../../operations/models/operations.models';
import { IFormMatchDataError } from '../../../../../operations/utils/form-operation-match-data.util';
import {
	buildFormMatchCompareTableRows,
	resolveFormMatchDataModalAlertKey,
	resolveFormMatchDataModalTitleKey,
} from '../../../../../operations/utils/form-operation-match-compare.util';
import { resolveFormOperationOcrPayload } from '../../../../../operations/utils/form-operation-detail-contract.util';
import { groupFormOperationAnswersBySection } from '../../../../../operations/utils/form-operation-detail-sections.util';
import { MasterService } from '../../../../../shared/services/master.service';
import { Permission } from '../../../../../core/models/role-permissions.models';
import { environment } from '../../../../../../environments/environment';
import { buildCurlGet } from '../../../../../panel/utils/build-curl.util';
import { ModalCurlRequestComponent } from '../../../modal-curl-request/modal-curl-request.component';

interface FormStepActivityModalInfo {
	title: string;
	step: IFormOperationAnswerStep;
	config?: IFormOperation['config'];
	matchDataErrors?: IFormMatchDataError[];
}

interface FormMatchCompareModalInfo {
	form: IOperationFormDetail;
	matchDataErrors?: IFormMatchDataError[];
}

@Component({
	selector: 'app-list-operations-activity',
	imports: [
		TranslatePipe,
		SafeDatePipe,
		DividerComponent,
		ItemComponent,
		ListDetailComponent,
		TagComponent,
		LinkComponent,
		EmptyStateComponent,
		ModalComponent,
		InfoGroupComponent,
		TileComponent,
		IconComponent,
		ButtonIconComponent,
		ModalJsonViewerComponent,
		FormOperationDetailSectionsComponent,
		AlertComponent,
		TableComponent,
		ModalCurlRequestComponent,
	],
	templateUrl: './list-operations-activity.component.html',
	styleUrl: './list-operations-activity.component.scss',
})
export class ListOperationsActivityComponent {
	@Input() data?: IProcessActivity[];
	@Input() logs_url?: string;
	@Input() curlPath = '';
	@Input() curlTitleKey = 'PROCESSES.CURL_MODAL.TITLE_ACTIVITY';

	public rolePermissions = inject(RolePermissionsService);
	private masterService = inject(MasterService);
	private languageService = inject(LanguageService);
	private trans = inject(TranslateService);

	public policyModalInfo: (PolicyModalInfo & { html: string }) | null = null;
	/** Respuesta JSON (p. ej. cierre de operación OCR/POL). */
	public activityJsonModal: { title: string; data: unknown } | null = null;
	public formStepModal: FormStepActivityModalInfo | null = null;
	public formMatchModal: FormMatchCompareModalInfo | null = null;
	public aeatModalOcr: IOperationOcrDetail | null = null;
	public menuItemAction = MenuItemAction;
	public booleanStatus = BOOLEAN_STATUS;

	public readonly curlModalOpen = signal(false);
	public readonly curlModalText = signal('');

	public canViewDeveloperCurl(): boolean {
		return this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION);
	}

	public openCurlModal(): void {
		if (!this.curlPath) {
			return;
		}
		this.curlModalText.set(buildCurlGet(environment.apiUrl, this.curlPath));
		this.curlModalOpen.set(true);
	}

	public closeCurlModal(): void {
		this.curlModalOpen.set(false);
	}

	public stripHtml(value?: string): string {
		if (!value) {
			return '';
		}
		const doc = new DOMParser().parseFromString(value, 'text/html');
		return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
	}

	public getTagIconItem(status: BOOLEAN_STATUS | string): ITagIcon {
		return status === 'OK'
			? { icon: 'icon-check', status: 'success', size: 'sm' }
			: { icon: 'icon-alert', status: 'error', size: 'sm' };
	}

	public canShowActivityLink(action: MenuItemAction): boolean {
		if (action === MenuItemAction.SHOW_JSON) {
			return this.rolePermissions.canViewOperationResponse();
		}
		return true;
	}

	public formStepModalSections(info: FormStepActivityModalInfo) {
		return groupFormOperationAnswersBySection([info.step], info.config, info.matchDataErrors);
	}

	public formMatchModalRows(info: FormMatchCompareModalInfo) {
		return buildFormMatchCompareTableRows(
			info.form,
			{
				translate: (key) => this.masterService.trans.instant(key),
				hasTranslation: (key) => this.masterService.hasTranslation(key),
				locale: this.languageService.getLanguage(),
			},
			info.matchDataErrors,
			resolveFormOperationOcrPayload(info.form),
		);
	}

	public formMatchModalHasMismatch(info: FormMatchCompareModalInfo): boolean {
		return this.formMatchModalRows(info).some((row) => row.notification);
	}

	public formMatchModalTitleKey(info: FormMatchCompareModalInfo): string {
		return resolveFormMatchDataModalTitleKey(info.form, resolveFormOperationOcrPayload(info.form));
	}

	public formMatchModalAlertKey(info: FormMatchCompareModalInfo): string {
		return resolveFormMatchDataModalAlertKey(info.form, resolveFormOperationOcrPayload(info.form));
	}

	/** Filas de cotejo OCR vs AEAT (NIF-1095). `resultado` no es cotejo → alert KO. */
	public aeatCompareRows(ocr: IOperationOcrDetail | null): Array<{
		clickValue: string;
		notification: boolean;
		cells: Array<{ label: string }>;
	}> {
		const details = ocr?.actions?.AEAT?.details;
		if (!details || !ocr) {
			return [];
		}

		const t = (key: string): string => this.trans.instant(key);
		return [
			{
				clickValue: '1',
				notification: ocr.names !== details.name,
				cells: [
					{ label: t('PROCESSES.DETAIL.AEAT_MODAL.NAME') },
					{ label: ocr.names ?? '' },
					{ label: details.name ?? '' },
				],
			},
			{
				clickValue: '2',
				notification: ocr.surnames !== details.surname,
				cells: [
					{ label: t('PROCESSES.DETAIL.AEAT_MODAL.SURNAMES') },
					{ label: ocr.surnames ?? '' },
					{ label: details.surname ?? '' },
				],
			},
			{
				clickValue: '3',
				notification: ocr.identifier !== details.identifier,
				cells: [
					{ label: t('PROCESSES.DETAIL.AEAT_MODAL.ID') },
					{ label: ocr.identifier ?? '' },
					{ label: details.identifier ?? '' },
				],
			},
		];
	}

	public showActivityModal(
		action: MenuItemAction,
		info:
			| PolicyModalInfo
			| { title: string; data: unknown }
			| FormStepActivityModalInfo
			| FormMatchCompareModalInfo
			| { ocr?: IOperationOcrDetail },
	): void {
		if (action === MenuItemAction.SHOW) {
			this.policyModalInfo = normalizePolicyModalInfo(info as PolicyModalInfo, this.languageService);
			return;
		}
		if (action === MenuItemAction.SHOW_JSON) {
			this.activityJsonModal = info as { title: string; data: unknown };
			return;
		}
		if (action === MenuItemAction.SHOW_FORM_STEP) {
			this.formStepModal = info as FormStepActivityModalInfo;
			return;
		}
		if (action === MenuItemAction.SHOW_FORM_MATCH_COMPARE) {
			this.formMatchModal = info as FormMatchCompareModalInfo;
			return;
		}
		if (action === MenuItemAction.SHOW_AEAT_COMPARE) {
			this.aeatModalOcr = (info as { ocr?: IOperationOcrDetail })?.ocr ?? null;
		}
	}

	public policyModalName(info: PolicyModalInfo): string {
		if (info.isExtra && !info.origin) {
			return this.trans.instant('PROCESSES.DETAIL.POLICY_AUTH_EXTRA');
		}
		if (info.custom_id === 'tecalis_default_check_1') {
			return this.trans.instant('PROCESSES.DETAIL.POLICY_TECALIS');
		}
		if (info.isExtra) {
			return this.trans.instant('PROCESSES.DETAIL.POLICY_AUTH_EXTRA');
		}
		return info.custom_id ?? '';
	}

	public policyModalOrigin(info: PolicyModalInfo): string {
		return policyOriginLabel(resolvePolicyOrigin(info), this.trans);
	}
}
